// ** React Imports
import { useContext } from "react";
import { useHistory, useParams } from "react-router-dom";

// ** Redux & Store & Actions
import { useSelector, useDispatch } from "react-redux";
import { getRankings } from "store/modules/main-pages/rankings/get-ranking.action";

// ** Third Party Components
import { useTranslation } from "react-i18next";

// ** Custom Components
import SelectorSelectSingle from "components/layout/DropdownSelectors/SelectorSelectSingle";

// ** Custom Hooks
import { compute as filterSexCompute } from "hooks/filters/useFilterSex";
import { compute as filterPeriodCompute } from "hooks/filters/useFilterPeriod";
import useDetectKeyboardOpen from "hooks/useDetectKeyboardOpen";

// ** Utils & Helpers
import { searchSexCustomSort } from "utils/searchSexCustomSort";
import generatePath from "utils/generatePath";
import { ANY, RANKING_PERIOD } from "helpers/constants";
import collatorSort from "utils/collatorSort";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";
import authContext from "contexts/authContext";

// #####################################################

const useRankingRegionSelector = ({ regionsList, selectedRegion }) => {
	const { isPhone } = useContext(deviceTypeContext);
	const isAuth = useContext(authContext);

	const { t } = useTranslation("common");
	const { push } = useHistory();

	const dispatch = useDispatch();

	const params = useParams();
	const {
		country: countryUrl,
		sex: sexUrl,
		period: periodUrl,
	} = params || {};

	const userSearchSex = useSelector((state) => state.globalUser.searchSex);
	const userSearchUrl = useSelector((state) => state.globalUser.urlSearch);
	const userCountryFromSearchUrl = userSearchUrl.split("/")[0];
	const countryWithRegions = useSelector(
		(state) => state.mainPageRankings.location?.countryWithRegions
	);

	const regions = countryWithRegions
		? regionsList.map((item) => ({
				...item,
				selected: selectedRegion !== item.id,
		  }))
		: [];

	const sortedItems = collatorSort(
		collatorSort(regions, "content"),
		"selected"
	);

	const { isKeyboardOpen, visualViewportHeight } = useDetectKeyboardOpen();

	const renderRegionSelector = (setFiltersOpen) =>
		countryWithRegions ? (
			<SelectorSelectSingle
				label={t("filtersAndSelectors:regionLabel")}
				emptyText={t("common:any.1")}
				items={sortedItems}
				selected={selectedRegion}
				onAccept={(item) => {
					const to = generatePath(
						"/rankings/:rankingPeriod/:sex/:country/:region",
						{
							rankingPeriod: periodUrl
								? periodUrl
								: RANKING_PERIOD.TODAY,
							country: countryUrl || userCountryFromSearchUrl,
							region: item?.url || ANY,
							sex: sexUrl
								? sexUrl
								: isAuth && userSearchSex.length > 0
								? [...userSearchSex]
										.sort(searchSexCustomSort)
										.join("-")
								: "", //"woman-man-couple-transsexual",
						}
					);
					dispatch(
						getRankings({
							apiParams: {
								period: filterPeriodCompute.urlToApiParam(
									periodUrl ? periodUrl : RANKING_PERIOD.TODAY
								),
								sex: filterSexCompute(true).urlToApiParam(
									sexUrl
										? sexUrl
										: isAuth
										? userSearchSex &&
										  userSearchSex.length > 0
											? [...userSearchSex]
													.sort(searchSexCustomSort)
													.join("-")
											: "woman-man-couple-transsexual"
										: "woman-man-couple-transsexual"
								),
								country: countryUrl || userCountryFromSearchUrl,
								region: item?.url || ANY,
							},
							useUrl: true,
						})
					);
					push(to);
					if (typeof setFiltersOpen === "function") {
						setFiltersOpen(false);
					}
				}}
				withMobileVersion
				withSearchInput
				isKeyboardOpen={isKeyboardOpen}
				visualViewportHeight={visualViewportHeight}
				customMobileLayerStyle={{
					display: "flex",
					flexDirection: "column",
				}}
				isSingleDropdown={true}
				autoFocus={isPhone}
				disabled={regionsList?.length === 0}
				withFullItem
			/>
		) : null;

	// #####################################################

	return {
		renderRegionSelector,
	};
};

export default useRankingRegionSelector;

// #####################################################
